<template>
  <div class="py-2 px-4 text-base-content/50 overflow-auto">
    <h3 class="text-lg mb-1">{{ $t(`game.values.title`) }}</h3>
    <p class="mb-2" v-html="$t(`game.values.tips`)"></p>

    <div class="border w-full">
      <ul class="flex gap-2.5 p-2.5 flex-shrink-0 overflow-auto">
        <li
          v-for="(item, idx) in [...store.form.characterzz, store.form.player]"
          :key="item.id"
          class="min-w-[68px] max-w-[68px] flex flex-col justify-center items-center"
        >
          <div
            :class="[
              'rounded-sm w-full  aspect-[3/4]',
              !item.isScoreEnabled && 'opacity-50',
            ]"
          >
            <BaseImg :media="store.characterIdMap[item.id!].avatarMedia" />
          </div>

          <p class="line-clamp-1 text-center mt-1">
            {{ getName(item) }}
          </p>
          <div>
            <van-checkbox
              v-model="item.isScoreEnabled"
              :icon-size="12"
              checked-color="#e84a4a"
              @change="store.saveBase"
            >
              <span :class="[!item.isScoreEnabled && 'text-my-gray']">
                {{ $t(`game.values.show`) }}
              </span>
            </van-checkbox>
          </div>
        </li>
      </ul>
    </div>

    <!-- 角色数值管理 -->
    <template
      v-for="(item, idx) in [...store.form.characterzz, store.form.player]"
    >
      <div
        v-if="item.isScoreEnabled"
        :key="item.id"
        class="flex gap-2.5 mt-2.5 bg-neutral p-2.5"
      >
        <!-- left -->
        <div class="w-[62px]">
          <BaseImg
            :media="store.characterIdMap[item.id!].avatarMedia"
            class="rounded-sm"
            ratio="3/4"
          />
          <p class="line-clamp-1 text-center">
            {{ getName(item) }}
          </p>
        </div>
        <!-- right -->
        <div
          :class="[
            'flex-1',
            store.scoreList.filter((v) => v.characterId === item.id).length ===
              0 && 'text-end',
          ]"
        >
          <ul>
            <template v-for="score in store.scoreList">
              <li v-if="score.characterId === item.id" :key="score.id">
                <div class="flex justify-between">
                  <div>
                    <p class="text-white">{{ score.name }}</p>
                    <p>
                      {{ $t(`game.values.init`) }}: {{ score.initialScore }}
                    </p>
                    <p>{{ score.description }}</p>
                  </div>
                  <div class="flex gap-1">
                    <button
                      class="btn btn-primary btn-xs"
                      @click="clickOpenEditValuesModal(score, item.id)"
                    >
                      <i class="iconify mingcute--edit-3-line text-xs"></i>
                    </button>
                    <button
                      class="btn btn-primary btn-xs"
                      @click="clickDeleteScore(score.id!)"
                    >
                      <i class="iconfont icon-delete text-xs"></i>
                    </button>
                  </div>
                </div>
              </li>
            </template>
          </ul>

          <button
            class="btn btn-xs mt-2.5"
            @click="clickOpenEditValuesModal(undefined, item.id)"
          >
            {{ $t(`game.values.add`) }}
          </button>
        </div>
      </div>
    </template>

    <!-- 其他数值 -->
    <div class="mt-2.5">
      <h3 class="text-xl">{{ $t(`game.values.other`) }}</h3>
      <ul class="mt-2.5">
        <template v-for="score in store.scoreList">
          <li
            v-if="!score.characterId"
            :key="score.id"
            class="flex justify-between bg-my-dark p-2.5 mb-2.5"
          >
            <div>
              <p class="text-white">{{ score.name }}</p>
              <p>{{ $t(`game.values.init`) }}: {{ score.initialScore }}</p>
              <p>{{ score.description }}</p>
            </div>
            <div class="flex gap-1">
              <button
                class="btn btn-primary btn-xs"
                @click="clickOpenEditValuesModal(score)"
              >
                <i class="iconify mingcute--edit-3-line text-xs"></i>
              </button>
              <button
                class="btn btn-primary btn-xs"
                @click="clickDeleteScore(score.id!)"
              >
                <i class="iconfont icon-delete text-xs"></i>
              </button>
            </div>
          </li>
        </template>
      </ul>
      <button class="btn btn-xs" @click="clickOpenEditValuesModal()">
        {{ $t(`game.values.add`) }}
      </button>
    </div>
  </div>

  <VanDialog v-model:show="showEditValuesModal">
    <template #title>
      <div class="text-center text-base font-bold">{{ t(``) }}</div>
    </template>
    <VanCellGroup inset>
      <VanField
        v-model="form.name"
        :label="t(`game.values.name`)"
        :placeholder="t(`game.values.nameTip`)"
        :rules="[{ required: true, message: $t(`game.values.rule.name`) }]"
      />
      <VanField
        v-model.number="form.initialScore"
        :label="t(`game.values.init`)"
        :placeholder="t(`game.values.initTip`)"
        :rules="[{ required: true, message: $t(`game.values.rule.initScore`) }]"
      />
      <VanField
        v-model="form.description"
        :label="t(`game.values.desc`)"
        :placeholder="t(`game.values.descTip`)"
        autoresize
        rows="3"
        type="textarea"
      />
    </VanCellGroup>

    <template #footer>
      <div class="flex justify-center py-4">
        <button class="btn btn-primary btn-sm mr-2" @click="clickConfirm">
          {{ t("Buttons.confirm") }}
        </button>

        <button class="btn btn-sm" @click="showEditValuesModal = false">
          {{ t(`story.g.cancel`) }}
        </button>
      </div>
    </template>
  </VanDialog>
</template>

<script lang="ts" setup>
import {
  ApiDeleteGalGameScore,
  ApiPostGalGameScore,
  ApiPutGalGameScore,
} from "@/apis/gal-game";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const store = useGalGameEditor();
const checked = ref(true);
const { t } = useI18n();

const showEditValuesModal = ref(false);
const form = ref<GalGameScore>({
  galGameId: 0,
  name: "",
  initialScore: 0,
  description: "",
});
const clickOpenEditValuesModal = (
  activeForm?: GalGameScore,
  characterId?: number
) => {
  if (activeForm) form.value = activeForm;
  else
    form.value = {
      galGameId: store.galGameId,
      characterId,
      name: "",
      initialScore: 0,
      description: "",
    };
  showEditValuesModal.value = true;
};

const getName = (item: GalGameCharacter) => {
  return store.characterIdMap[item.id!].name?.trim()
    ? store.characterIdMap[item.id!].name
    : t(`game.role.${item?.role}`);
};

const clickConfirm = async () => {
  const { id, name, initialScore } = form.value;
  if (!name) return showFailToast(t(`game.values.rule.name`));
  if (typeof initialScore === undefined)
    return showFailToast(t(`game.values.rule.initScore`));
  const loading = showLoadingToast(t(`Message.loading`));
  if (!id) {
    const { item } = await ApiPostGalGameScore(form.value);
    store.scoreList.push(item);
  } else {
    const { item } = await ApiPutGalGameScore(id, form.value);
    store.scoreList = store.scoreList.map((v) => (v.id === id ? item : v));
  }
  loading.close();
  showEditValuesModal.value = false;
};

const clickDeleteScore = async (id: number) => {
  try {
    await showConfirmDialog({
      title: t("game.values.deleteTitle"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    const loading = showLoadingToast(t(`Message.loading`));
    await ApiDeleteGalGameScore(id);
    store.scoreList = store.scoreList.filter((v) => v.id !== id);
    loading.close();
  } catch (e) {}
};
</script>
